import React, { useEffect, useState } from 'react';

// Customer-facing push notification opt-in. Drop into account / settings page.
// Handles: permission request, subscription registration with our backend,
// segment toggles (drops, orders, marketing), and unsubscribe.

const SEGMENTS = [
  { key: 'drops',     label: 'Drops & restock alerts',  desc: 'Pokemon Center, Best Buy, Walmart restocks within seconds' },
  { key: 'orders',    label: 'Order updates',           desc: 'Shipping, delivery, exceptions' },
  { key: 'marketing', label: 'Sales & new arrivals',    desc: 'Promotions, new sealed products, weekly deals' },
];

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export default function PushOptIn() {
  const [supported, setSupported]     = useState(false);
  const [permission, setPermission]   = useState('default');
  const [subscribed, setSubscribed]   = useState(false);
  const [endpoint, setEndpoint]       = useState(null);
  const [segments, setSegments]       = useState(['drops', 'orders']);
  const [busy, setBusy]               = useState(false);
  const [msg, setMsg]                 = useState('');

  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    navigator.serviceWorker.getRegistration().then(async (reg) => {
      const sub = await reg?.pushManager.getSubscription();
      if (sub) { setSubscribed(true); setEndpoint(sub.endpoint); }
    }).catch(() => {});
  }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') { flash('Permission denied'); return; }

      const keyRes = await fetch('/api/push/vapid-public');
      const { key } = await keyRes.json();
      if (!key) { flash('Push not configured server-side'); return; }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });

      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const r = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ subscription: sub.toJSON(), segments }),
      });
      if (!r.ok) { flash('Subscribe failed'); return; }
      setSubscribed(true);
      setEndpoint(sub.endpoint);
      flash('Notifications enabled');
    } catch (e) {
      flash('Failed: ' + e.message);
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await fetch('/api/push/unsubscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setEndpoint(null);
      flash('Notifications disabled');
    } finally { setBusy(false); }
  };

  const updateSegments = async (next) => {
    setSegments(next);
    if (!subscribed || !endpoint) return;
    // Re-POST subscribe with new segments (idempotent on endpoint)
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ subscription: sub.toJSON(), segments: next }),
    }).catch(() => {});
  };

  const toggleSegment = (k) => {
    const next = segments.includes(k) ? segments.filter(x => x !== k) : [...segments, k];
    updateSegments(next);
  };

  if (!supported) {
    return (
      <div style={{ padding: 16, background: 'var(--bg-hover)', borderRadius: 8 }}>
        <strong>Push notifications not supported in this browser.</strong>
        <p style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 4 }}>
          Push works in Chrome, Edge, Firefox, and (after install) Safari iOS 16.4+. Try installing SilverGames to your home screen for full support on iOS.
        </p>
      </div>
    );
  }

  return (
    <div>
      {msg && <div style={{ padding: 8, background: 'var(--color-info-bg)', borderRadius: 6, marginBottom: 8 }}>{msg}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <strong style={{ fontSize: 16 }}>Push notifications</strong>
        <span style={{
          fontSize: 12, padding: '2px 10px', borderRadius: 12,
          background: subscribed ? '#16a34a' : '#94a3b8', color: '#fff',
        }}>{subscribed ? 'Enabled' : 'Disabled'}</span>
      </div>

      {!subscribed ? (
        <button className="btn btn-primary" onClick={enable} disabled={busy}>
          {busy ? 'Working…' : 'Enable push notifications'}
        </button>
      ) : (
        <>
          <p style={{ fontSize: 13, color: 'var(--text-light)' }}>
            Choose what you want to be notified about:
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            {SEGMENTS.map(s => (
              <label key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={segments.includes(s.key)}
                       onChange={() => toggleSegment(s.key)} style={{ marginTop: 3 }} />
                <div>
                  <div><strong>{s.label}</strong></div>
                  <small style={{ color: 'var(--text-light)' }}>{s.desc}</small>
                </div>
              </label>
            ))}
          </div>
          <button className="btn btn-secondary" onClick={disable} disabled={busy} style={{ marginTop: 16 }}>
            {busy ? 'Working…' : 'Disable push notifications'}
          </button>
        </>
      )}

      {permission === 'denied' && (
        <p style={{ marginTop: 12, fontSize: 13, color: 'var(--color-danger)' }}>
          Notifications were blocked in your browser. Enable them in your browser's site settings, then return here.
        </p>
      )}
    </div>
  );
}
